import { useEffect, useMemo, useState } from 'react';
import {
	all,
	getCategoryOptions,
	SetExceptionsHidden,
	VelocityFeedAuditTab,
} from '@/api/audit/velocity-feed-audit';
import { Button } from '@/components/ui/button';
import { MultiSelectDropdown } from '@/components/ui/custom/multi-select-dropdown';
import { DateRangePickerSplit } from '@/components/ui/date-range-picker-split';
import { SearchBar } from '@/components/ui/SearchBar/SearchBar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
	keepPreviousData,
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query';
import { PaginationState, SortingState } from '@tanstack/react-table';
import { toast } from 'sonner';

import { VelocityFeedAuditTable } from './table';
import { TransactionQueueTable } from './transaction-queue-table';

const DEFAULT_PAGINATION: PaginationState = {
	pageIndex: 0,
	pageSize: 10,
};

const DEFAULT_SORTING: SortingState = [{ id: 'errorDate', desc: true }];

const DEFAULT_SELECTED_CATEGORIES = ['Policy', 'Transaction'];

type VelocityFeedAuditPageTab = 'transaction-queue' | VelocityFeedAuditTab;

const isTabValue = (value: string): value is VelocityFeedAuditTab =>
	value === 'active' || value === 'hidden' || value === 'resolved';

const isPageTabValue = (value: string): value is VelocityFeedAuditPageTab =>
	value === 'transaction-queue' || isTabValue(value);

export function VelocityFeedAuditTabs() {
	const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
	const [globalSearch, setGlobalSearch] = useState('');
	const [tabSearch, setTabSearch] = useState('');
	const [startDate, setStartDate] = useState<Date | null>(null);
	const [endDate, setEndDate] = useState<Date | null>(null);
	const [pagination, setPagination] =
		useState<PaginationState>(DEFAULT_PAGINATION);
	const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);
	const [currentTab, setCurrentTab] =
		useState<VelocityFeedAuditPageTab>('transaction-queue');
	const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
	const [categoryOptions, setCategoryOptions] = useState<string[]>([]);
	const isErrorTab = isTabValue(currentTab);

	useEffect(() => {
		const fetchCategoryOptions = async () => {
			try {
				const categories = await getCategoryOptions();
				setCategoryOptions(categories);
				setSelectedCategories(
					DEFAULT_SELECTED_CATEGORIES.filter((category) =>
						categories.includes(category),
					),
				);
			} catch (err) {
				console.error('Failed to load category options:', err);
				setCategoryOptions([]);
			}
		};

		fetchCategoryOptions();
	}, []);

	const resetPaginationToFirstPage = () => {
		setPagination((prev) => ({
			...prev,
			pageIndex: 0,
		}));
	};

	const resetTabScopedFilters = () => {
		setTabSearch('');
		setStartDate(null);
		setEndDate(null);
		setSelectedCategories(
			DEFAULT_SELECTED_CATEGORIES.filter((category) =>
				categoryOptions.includes(category),
			),
		);
		resetPaginationToFirstPage();
	};

	const queryClient = useQueryClient();
	const { data, error, isError, isLoading, isFetching } = useQuery({
		queryKey: [
			'velocity-feed-audit',
			globalSearch,
			currentTab,
			tabSearch,
			startDate,
			endDate,
			pagination,
			sorting,
			selectedCategories.join(','),
		],
		queryFn: () => {
			if (!isTabValue(currentTab)) {
				throw new Error('Invalid velocity feed audit error tab.');
			}

			return all(
				globalSearch,
				currentTab,
				tabSearch,
				startDate,
				endDate,
				sorting,
				pagination,
				selectedCategories,
			);
		},
		enabled: isErrorTab,
		placeholderData: keepPreviousData,
		staleTime: 30_000,
	});

	const mutation = useMutation({
		mutationFn: ({ ids, hide }: { ids: number[]; hide: boolean }) =>
			SetExceptionsHidden(ids, hide),
		onSuccess: async (_, { hide }) => {
			await queryClient.invalidateQueries({ queryKey: ['velocity-feed-audit'] });
			setRowSelection({});
			toast.success(
				hide
					? 'Successfully hid selected exceptions.'
					: 'Successfully unhid selected exceptions.',
			);
		},
		onError: (_, { hide }) => {
			toast.error(
				hide ? 'Failed to hide exceptions.' : 'Failed to unhide exceptions.',
			);
		},
	});

	const handleHideToggle = (selectedIds: number[]) => {
		if (selectedIds.length === 0) return;
		const hide = currentTab === 'active';
		mutation.mutate({ ids: selectedIds, hide });
	};

	const handleSingleHide = (id: number) => {
		mutation.mutate({ ids: [id], hide: true });
	};

	const selectedIds = useMemo(
		() =>
			Object.entries(rowSelection)
				.filter(([_, selected]) => selected)
				.map(([id]) => Number(id)),
		[rowSelection],
	);

	return (
		<>
			<div className="mb-4 flex justify-center">
				<SearchBar
					placeholder="Search Errors..."
					onSearch={(searchTerm) => {
						setGlobalSearch(searchTerm);
						if (!isTabValue(currentTab)) setCurrentTab('active');
						resetPaginationToFirstPage();
					}}
					isLoading={isErrorTab && isLoading}
				/>
			</div>
			<Tabs
				value={currentTab}
				onValueChange={(val) => {
					if (!isPageTabValue(val)) return;
					setCurrentTab(val);
					setRowSelection({});
					if (isTabValue(val)) resetTabScopedFilters();
				}}
				className="w-full"
			>
				<TabsList className="mb-4">
					<TabsTrigger value="transaction-queue">Transaction Queue</TabsTrigger>
					<TabsTrigger value="active">Active Errors</TabsTrigger>
					<TabsTrigger value="hidden">Hidden Errors</TabsTrigger>
					<TabsTrigger value="resolved">Resolved Errors</TabsTrigger>
				</TabsList>

				<TabsContent value="transaction-queue">
					<TransactionQueueTable />
				</TabsContent>

				{isErrorTab && (
					<TabsContent value={currentTab}>
						{isError && (
							<div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
								Failed to load velocity feed audit data.
							</div>
						)}
						<div className="mb-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
							<div className="flex flex-col md:flex-row md:items-center md:gap-4">
								<SearchBar
									placeholder={`Search ${currentTab} tab...`}
									onSearch={(searchTerm) => {
										setTabSearch(searchTerm);
										resetPaginationToFirstPage();
									}}
									isLoading={isLoading}
									className="w-52"
									value={tabSearch}
								/>
								<div className="flex items-center gap-2">
									<DateRangePickerSplit
										startDate={startDate}
										endDate={endDate}
										onChange={(start, end) => {
											setStartDate(start);
											setEndDate(end);
											resetPaginationToFirstPage();
										}}
									/>
									<Button
										variant="outline"
										onClick={() => {
											setStartDate(null);
											setEndDate(null);
											resetPaginationToFirstPage();
										}}
										disabled={!startDate && !endDate}
									>
										Clear Date
									</Button>
								</div>
								<MultiSelectDropdown
									label="Categories"
									options={categoryOptions}
									selected={selectedCategories}
									onChange={(selected) => {
										setSelectedCategories(selected);
										resetPaginationToFirstPage();
									}}
									itemClassName="capitalize"
									className="w-full sm:w-56 md:w-64 lg:w-80"
								/>
							</div>

							{(currentTab === 'active' || currentTab === 'hidden') && (
								<Button
									onClick={() => handleHideToggle(selectedIds)}
									disabled={selectedIds.length === 0 || mutation.isPending}
									className="self-end md:self-auto"
								>
									{mutation.isPending
										? 'Updating...'
										: currentTab === 'active'
											? 'Hide Selected'
											: 'Unhide Selected'}
								</Button>
							)}
						</div>

						<VelocityFeedAuditTable
							data={data?.data || []}
							totalCount={data?.total || 0}
							pagination={pagination}
							setPagination={setPagination}
							sorting={sorting}
							setSorting={setSorting}
							isActiveTab={currentTab === 'active'}
							rowSelection={rowSelection}
							setRowSelection={setRowSelection}
							onSingleHide={handleSingleHide}
							isFetching={isFetching}
						/>
					</TabsContent>
				)}
			</Tabs>
		</>
	);
}
